import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import InterviewReport from "@/components/InterviewReport";
import {
  GraduationCap, Loader2, Send, ShieldCheck, Lock, Sparkles, History, ChevronRight, Star,
} from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

const REGULATORS = [
  { id: "CQC", title: "CQC Registered Manager", body: "Fit-person interview covering the five key questions, Regulation 7 and well-led leadership.", lookup: "mock_interview_cqc" },
  { id: "Ofsted", title: "Ofsted Registered Manager", body: "Suitability interview covering the SCCIF, Children's Homes Regulations 2015 and the Quality Standards.", lookup: "mock_interview_ofsted" },
  { id: "Fostering", title: "Ofsted IFA Registered Manager", body: "Registration interview for independent fostering agencies: Fostering Regulations 2011, National Minimum Standards, panel and Form F, matching, safer caring and allegations.", lookup: "mock_interview_fostering" },
  { id: "Healthcare", title: "CQC GP & Hospital Manager", body: "Fit-person interview for practice managers, partners and independent hospital registered managers: clinical governance, medicines, safeguarding, significant events and leadership.", lookup: "mock_interview_healthcare" },
  { id: "Aesthetics", title: "CQC Aesthetics Clinic Manager", body: "Fit-person interview for aesthetics clinics: regulated activities, prescribing governance, consent, complications and clinical governance.", lookup: "mock_interview_aesthetics" },
];

function ScoreStars({ score }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="turn-score">
      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(score / 2) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />)}
      <span className="text-xs text-muted-foreground ml-1">{score}/10</span>
    </span>
  );
}

export default function MockInterview() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [access, setAccess] = useState(null);
  const [history, setHistory] = useState([]);
  const [interview, setInterview] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(null);
  const bottomRef = useRef(null);

  const loadMeta = useCallback(() => Promise.all([
    api.get("/interviews/access").then((r) => setAccess(r.data)),
    api.get("/interviews/mine").then((r) => setHistory(r.data)),
  ]), []);

  useEffect(() => {
    loadMeta();
    const id = params.get("id");
    if (id) api.get(`/interviews/${id}`).then((r) => setInterview(r.data)).catch((err) => logError("Failed to load interview", err));
  }, [params, loadMeta]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [interview?.turns?.length, busy]);

  const start = async (regulator) => {
    setStarting(regulator);
    try {
      const { data } = await api.post("/interviews/start", { regulator });
      setInterview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setStarting(null); }
  };

  const submit = async () => {
    if (!answer.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/interviews/${interview.id}/answer`, { answer });
      setInterview(data); setAnswer("");
      if (data.status === "completed") { toast.success("Interview complete — your report is ready"); loadMeta(); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const canStart = (r) => access?.[r] || access?.quarterly_free_available;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="overline text-accent">AI Mock Interview</span>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Practise your registered manager interview</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            A realistic {interview?.regulator || "CQC / Ofsted"} inspector asks {6} questions, scores every answer and produces a written readiness report.
          </p>
        </div>
        {interview && (
          <Button variant="outline" onClick={() => setInterview(null)} data-testid="interview-back-button">
            <History className="w-4 h-4 mr-1.5" /> All interviews
          </Button>
        )}
      </div>

      {!interview ? (
        <>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {REGULATORS.map((r) => {
              const ok = canStart(r.id);
              return (
                <div key={r.id} className="bg-card border border-border rounded-2xl p-6 shadow-sm flex flex-col" data-testid={`interview-option-${r.id.toLowerCase()}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-primary" /></div>
                    <Badge variant="secondary">{r.id}</Badge>
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-1">{r.title}</h3>
                  <p className="text-sm text-muted-foreground flex-1 mb-5">{r.body}</p>
                  {ok ? (
                    <Button onClick={() => start(r.id)} disabled={!!starting} data-testid={`start-interview-${r.id.toLowerCase()}`}>
                      {starting === r.id ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                      Start interview {access?.[r.id] ? "" : "(free quarterly)"}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Button asChild variant="outline" data-testid={`unlock-interview-${r.id.toLowerCase()}`}>
                        <Link to="/resources"><Lock className="w-4 h-4 mr-1.5" /> Unlock for £250</Link>
                      </Button>
                      <p className="text-xs text-muted-foreground">Unlimited mock interviews are included with <Link to="/pricing" className="text-primary hover:underline">Enterprise</Link>.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><History className="w-5 h-5 text-primary" /> Past interviews</h2>
          {history.length ? (
            <div className="space-y-2" data-testid="interview-history">
              {history.map((h) => (
                <button key={h.id} onClick={() => api.get(`/interviews/${h.id}`).then((r) => setInterview(r.data))}
                  className="w-full flex items-center justify-between gap-3 bg-card border border-border rounded-xl px-4 py-3 text-left hover:border-primary/50 transition-colors"
                  data-testid={`interview-history-${h.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="secondary">{h.regulator}</Badge>
                    <span className="text-sm">{new Date(h.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    {h.status === "completed"
                      ? <Badge className="bg-accent/10 text-accent border-0">{h.report?.grade} · {h.report?.overall_score}%</Badge>
                      : <Badge variant="outline">In progress</Badge>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No interviews yet — pick a regulator above to begin.</p>}
        </>
      ) : interview.status === "completed" ? (
        <InterviewReport interview={interview} onRestart={() => start(interview.regulator)} canRestart={canStart(interview.regulator)} />
      ) : (
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden" data-testid="interview-session">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="secondary">{interview.regulator}</Badge>
              <span className="text-muted-foreground" data-testid="interview-progress-label">Question {interview.turns.length} of {interview.total}</span>
            </div>
            <Progress value={((interview.turns.length - 1) / interview.total) * 100} className="h-1.5 w-40" />
          </div>
          <div className="p-6 space-y-6 max-h-[55vh] overflow-y-auto">
            {interview.turns.map((t, i) => (
              <div key={`${interview.id}-q${i + 1}`} className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center shrink-0"><ShieldCheck className="w-4 h-4 text-accent" /></div>
                  <div className="bg-secondary rounded-2xl px-4 py-3 text-sm leading-relaxed max-w-[85%]" data-testid={`interview-question-${i + 1}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Inspector · Q{i + 1}</p>
                    {t.question}
                  </div>
                </div>
                {t.answer && (
                  <div className="flex gap-3 flex-row-reverse">
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0 text-primary-foreground text-xs font-bold">{(user?.name || "Y")[0]}</div>
                    <div className="bg-primary text-primary-foreground rounded-2xl px-4 py-3 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap">{t.answer}</div>
                  </div>
                )}
                {t.feedback && (
                  <div className="ml-11 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-sm" data-testid={`interview-feedback-${i + 1}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-accent">Feedback</p>
                      <ScoreStars score={t.score} />
                    </div>
                    <p className="text-muted-foreground">{t.feedback}</p>
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="ml-11 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> The inspector is considering your answer…</div>}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-border p-4">
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} disabled={busy}
              placeholder="Answer as you would in the real interview — be specific, use examples and reference the regulations where you can."
              className="resize-none mb-3" data-testid="interview-answer-input" />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">{answer.length}/4000</p>
              <Button onClick={submit} disabled={busy || !answer.trim()} data-testid="interview-submit-answer-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Submit answer</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
